import React, { useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import { FullPageSpinner } from "components/Spinner";
import { FLOW_STATE, useAnalytics } from "utils";
import { useAccountKYCStatus } from "api/kyc";
import EmptyState from "components/EmptyState";
import { useFlowConfig } from "flows/hooks";

import { useSettings } from "utils/settingsContext";

import KYC2Form from "./Form";
import PostSubmissionPolling from "./Form/PostSubmissionPolling";
import Success from "./Success";

/**
 * KYC2
 * @description
 * This component handles checking of current KYC status for user and then either sends them
 * on their way, or renders the KYC form for them to fill out and submit.
 *
 * @todo Rename this to KYC when old KYC has been fully deprecated
 */
export default function KYC2({ onComplete, onReady, onError }) {
  const { formatMessage } = useIntl();
  const { appSettings, integratorSettings } = useSettings();
  const { verificationLevel } = useFlowConfig();

  const config = {
    ...(appSettings?.KYC || {}),
    sanctionedCountries: appSettings?.sanctionedCountries || [],
    ...(integratorSettings?.KYC || {}),
    support_url: integratorSettings?.support_url || null,
  };

  const { data, isPending: isLoading, error, isError } = useAccountKYCStatus();
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("KYC", "KYC_Viewed", { verificationLevel }); // TODO: this may be a superflous analytic as it should be followed by an event for whatever section the user is taken to.  Have a feeling it could be useful though
      viewEventFired.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, viewEventFired]);

  useEffect(() => {
    onReady({ state: FLOW_STATE.READY });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <FullPageSpinner data-testid="kyc-status-spinner" />;
  }

  if (isError) {
    return <EmptyState error={error} />;
  }

  if (data) {
    let state = data.payload?.state || undefined; // current state of full KYC for user
    const kycId = data.payload?.kyc_id || undefined;

    // check for handling basic KYC (no KYC application required, no running through Acuant)
    const { pii_level, kyc_requirements } = data.payload || {};
    const noKYCRequirementsSetForUser = kyc_requirements === "NONE" || !kyc_requirements;
    const integratorRequestsBasicPII = verificationLevel === "basic_pii" || integratorSettings?.feature_flags?.prefer_basic_pii_kyc_when_possible; // Integrator based 'prefer_basic_pii_kyc_when_possible' flag to allow KYC lite if possible
    const limitToBasicPII = noKYCRequirementsSetForUser && integratorRequestsBasicPII;
    const userHasCompleteBasicPII = pii_level === "BASIC";
    if (limitToBasicPII && userHasCompleteBasicPII) {
      state = "BASIC_PII_COLLECTED"; // this is a UI generated state only, that is only used in the UI
    }
    if (data.payload.kyc_requirements === "TAX_UPDATE") {
      state = "TAX_UPDATE";
    }

    switch (state) {
      case "NODATA":
      case "DENY":
      case "CANCEL":
      case "REVERIFY":
      case "TAX_UPDATE":
      case "NONE": // this gets set when basic PII info on the user has been collected, but we haven't run them through full KYC.  If all we need is basic PII to advance that will be covered by the generated state: BASIC_PII_COLLECTED
      case "SYSTEM-CANCELLED": // this gets set when a KYC application fails, but the user can retry submission by modifying form
        return (
          <KYC2Form
            onComplete={onComplete}
            onError={onError}
            config={config}
            limitToBasicPII={limitToBasicPII}
            condition={state}
          />
        );
      case "MANUAL_REVIEW":
        return <Success applicationState="MANUAL_REVIEW" onComplete={onComplete} />;
      case "PROCESSING":
        return (
          <PostSubmissionPolling
            kycId={kycId}
            onComplete={onComplete}
            onError={onError}
            loadingMessages={[
              formatMessage({
                defaultMessage: "Picking up where we left off...",
                description: "Loading message",
              }),
              formatMessage({
                defaultMessage: "Processing...",
                description: "Loading message",
              }),
              formatMessage({
                defaultMessage:
                  "Hmm, this is taking awhile. We’ll keep waiting for results, but you’ll also receive an email once we verify your identity.",
                description: "Loading message",
              }),
            ]}
          />
        );
      case "ACCEPT":
      case "BASIC_PII_COLLECTED":
      case "TAX_UPDATE_COLLECTED":
        onComplete({ state: FLOW_STATE.COMPLETE, result: state });
        return null;
      default:
        console.error("KYC is in an unknown state:", state);

        return (
          <EmptyState
            title={formatMessage({
              defaultMessage: "Oops!",
              description: "Informal expression for a minor mistake",
            })}
            description={formatMessage({
              defaultMessage:
                "We are unable to determine your identity verification at the moment. Please try again later.",
              description: "Error message",
            })}
          />
        );
    }
  }

  return null;
}
